
import React from 'react';
import SEO from '../components/SEO';
import { MODULES, PLATFORM_CAPABILITIES, CapabilityArea } from '../constants';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import ImageCarousel, { usePublicImages } from '../components/ImageCarousel';
import WorkflowTimeline from '../components/WorkflowTimeline';
import FAQSection from '../components/FAQSection';

const EAIPPage: React.FC = () => {
  const [query, setQuery] = React.useState('');
  const [area, setArea] = React.useState<'All' | CapabilityArea>('All');
  const moduleData = MODULES.find(m => m.id === 'eaip')!;

  const eaipFAQ = [
    {
      question: "How does AI NOTAM generation work?",
      answer: "The AviaGov AI assistant supports drafting and formatting based on safety findings or procedure changes, helping align content with ICAO NOTAM structure. All outputs remain subject to human review and authority approval."
    },
    {
      question: "Can we automate the translation of the AIP?",
      answer: "Translation support is available to assist with consistent terminology and formatting across ICAO languages. Final wording and publication remain under authority control and approval."
    },
    {
      question: "Does it support the full AIRAC cycle?",
      answer: "Yes. AviaGov manages the 28-day/56-day AIRAC cycles with validation checkpoints, controlled release gates, and formal approval workflows."
    }
  ];

  const eaipCarouselImages = usePublicImages('eaip', 'eAIP interface screen');
  const visibleCapabilities = PLATFORM_CAPABILITIES.filter((capability) => {
    const matchesArea = area === 'All' || capability.area === area;
    const haystack = `${capability.name} ${capability.description} ${capability.sourceSignals.join(' ')}`.toLowerCase();
    return matchesArea && haystack.includes(query.toLowerCase());
  });

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="eAIP Software for Civil Aviation Authorities and ANSPs"
        description="ICAO Annex 15 aligned eAIP software for AIM teams managing AIP amendments, NOTAM preparation, AIRAC cycles, validation, and public aeronautical publication."
        ogType="product"
        faq={eaipFAQ}
      />

      <section className="py-24 bg-slate-950 border-b border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div className="relative z-10">
            <nav className="flex items-center space-x-2 text-slate-500 text-sm mb-8 uppercase tracking-widest font-bold">
              <span>Modules</span>
              <span className="text-accent">/</span>
              <span className="text-accent">eAIP Platform</span>
            </nav>
            <h1 className="text-5xl lg:text-[6rem] font-bold font-heading mb-10 leading-[0.9] text-white tracking-tighter">
              eAIP software for <br />
              <span className="text-accent">authorities and ANSPs.</span>
            </h1>
            <p className="text-xl text-slate-400 mb-12 leading-relaxed">
              Replace manual document handling with a controlled ICAO Annex 15 data chain for AIP amendments, NOTAM preparation, AIRAC management, validation, and public publication.
            </p>
            <p className="text-sm text-slate-500 mb-4 leading-relaxed">
              Designed for Civil Aviation Authorities, ANSPs, and Airports responsible for regulated aeronautical information publication.
            </p>
            <p className="text-sm text-slate-500 italic mb-12 leading-relaxed">
              All NOTAMs, AIP amendments, and publications remain subject to formal authority validation and approval. AI capabilities provide drafting and formatting assistance only and do not publish autonomously.
            </p>
            <div className="flex flex-wrap gap-4 mb-12">
              {moduleData.metrics?.map((m, i) => (
                <GlowCard key={i} className="px-6 py-3 bg-accent/5 border border-accent/20 rounded-2xl">
                  <div className="text-2xl font-bold text-accent">{m.value}</div>
                  <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">{m.label}</div>
                </GlowCard>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-10 bg-accent/5 blur-[100px] rounded-full"></div>
            <AIImage 
              prompt="A 3D cartoon style digital document tablet showing an aeronautical chart with neon lines, floating in a high-tech control center, vibrant cyan accents."
              alt="eAIP Digital Charting"
              className="w-full aspect-[4/3] relative z-10 shadow-2xl"
              aspectRatio="4:3"
              src="/eaip.png"
            />
          </div>
        </div>
      </section>

      {/* Integration highlight */}
      <section className="py-24 px-4 bg-surface/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold font-heading mb-12 text-white">Connected Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {moduleData.connectedFeatures.map((cf, i) => (
              <GlowCard key={i} className="p-8 bg-slate-900 border border-white/5 rounded-3xl flex items-center space-x-8">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center text-accent font-bold text-sm flex-shrink-0">
                  {cf.targetModule}
                </div>
                <div>
                  <h4 className="text-white font-bold mb-2">{cf.title}</h4>
                  <p className="text-slate-500 text-sm">{cf.description}</p>
                </div>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-6 text-white tracking-tight">Core Publication Features</h2>
            <p className="text-slate-500">Authority-grade NOTAM preparation and AIRAC control with assisted publication support.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {moduleData.features.map((feature, i) => (
              <GlowCard key={i} delay={i * 50}>
                <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950 border-y border-white/5" aria-labelledby="capability-inventory-title">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-10">
            <p className="text-accent text-xs uppercase tracking-[0.2em] font-bold mb-4">Platform capability inventory</p>
            <h2 id="capability-inventory-title" className="text-3xl lg:text-5xl font-bold font-heading text-white mb-5">One operational map for every aviation workflow.</h2>
            <p className="text-slate-400 leading-relaxed">The eAIP source model spans publication, operations, procedure design, safety, training, connectivity and assurance. Explore the complete capability surface below.</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <label className="sr-only" htmlFor="capability-search">Search capabilities</label>
            <input id="capability-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search capabilities, workflows or source signals" className="flex-1 bg-slate-900 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-accent" />
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter capability area">
              {(['All', 'AIM', 'Procedure studio', 'Safety & training', 'Connectivity & governance'] as const).map((value) => (
                <button key={value} type="button" onClick={() => setArea(value)} aria-pressed={area === value} className={`px-3 py-2 rounded-xl text-xs font-bold transition-colors ${area === value ? 'bg-accent text-slate-950' : 'bg-slate-900 text-slate-400 border border-white/10 hover:text-white'}`}>{value}</button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-5" aria-live="polite">Showing {visibleCapabilities.length} of {PLATFORM_CAPABILITIES.length} capabilities</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleCapabilities.map((capability) => (
              <article key={capability.key} className="rounded-2xl border border-white/10 bg-slate-900/70 p-6 hover:border-accent/40 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-4"><h3 className="text-white font-bold">{capability.name}</h3><span className="text-[10px] uppercase tracking-wider text-accent whitespace-nowrap">{capability.area}</span></div>
                <p className="text-sm text-slate-400 leading-relaxed mb-5">{capability.description}</p>
                <div className="flex flex-wrap gap-2" aria-label="Source signals">{capability.sourceSignals.map((signal) => <span key={signal} className="text-[10px] text-slate-500 bg-slate-950 border border-white/5 rounded-md px-2 py-1">{signal}</span>)}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <ImageCarousel
        title="eAIP Interface Preview"
        description="Screens from the eAIP publication workspace, AIRAC control, and compliance views."
        images={eaipCarouselImages}
      />

      {/* Workflow Highlight */}
      <section className="py-24 bg-primary border-y border-white/5">
        <GlowCard className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold font-heading mb-8 text-center text-white">Controlled Digital Publication Workflow</h2>
          <WorkflowTimeline />
        </GlowCard>
      </section>

      <section className="py-24 px-4 bg-surface/30">
        <GlowCard className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold font-heading mb-8 text-white">Migration & Authority Adoption</h2>
          <ul className="text-slate-400 space-y-3 text-sm leading-relaxed">
            <li>Legacy AIP import (PDF / Word / HTML)</li>
            <li>Parallel AIRAC operation</li>
            <li>Controlled go-live by AIRAC cycle</li>
            <li>Training and onboarding support</li>
          </ul>
        </GlowCard>
      </section>

      <FAQSection items={eaipFAQ} title="eAIP Technical FAQ" />
    </div>
  );
};

export default EAIPPage;
